import DataImplementationNormalization from './DataImplementationNormalization.js';
import DataImplementationOverride from './DataImplementationOverride.js';
import DataImplementationSelection from './DataImplementationSelection.js';
import DataRecord from './DataRecord.js';
import DataReferenceSourceType from './DataReferenceSourceType.js';
/**
 * @memberof data-references
 * @classdesc
 * A {@linkcode DataImplementation} represents the usage & configuration of one
 * or more {@linkcode data-references.DataReference DataReference}. The
 * variables used are described and represented by the
 * {@linkcode data-references.DataReferenceCollection DataReferenceCollection}
 * specified by the "ref" property.
 * <br><br>
 * Each {@linkcode DataImplementation} can be optionally normalized by
 * populating the appropriate fields of the
 * {@linkcode data-references.DataImplementationNormalization Normalization} class
 * specified by the "normalization" property.
 *
 * @property {data-references.DataReferenceCollection} ref - Valid as a "pointer" or "populated" collection
 * @property {data-references.DataImplementationOverride[]} overrides - An optional list of overrides
 * @property {data-references.DataImplementationNormalization} normalization - An optional specifier for normalizing variables
 * @property {data-references.DataImplementationSelection} selection - An optional description of source for selection
 */
class DataImplementation {
    /**
     * @param {object} props
     * @param {data-references.DataReferenceCollection} props.ref
     * @param {data-references.DataImplementationOverride[]} [props.overrides]
     * @param {data-references.DataImplementationNormalization} [props.normalization]
     * @param {data-references.DataImplementationSelection} [props.selection]
     */
    constructor(props) {
        this.ref = props.ref;
        this.normalization = props.normalization ? new DataImplementationNormalization(props.normalization) : new DataImplementationNormalization();
        this.overrides = (props.overrides || []).map(props => new DataImplementationOverride(props));
        this.selection = props.selection || new DataImplementationSelection();
    }
    /**
     * @type {boolean}
     */
    get isHydrated() {
        return this.ref && this.ref.variables.length > 0;
    }
    /**
     * @type {boolean}
     */
    get isDehydrated() {
        return !this.isHydrated;
    }
    /**
     * @type {boolean}
     */
    get hasNormalization() {
        return this.normalization.isPopulated;
    }
    /**
     * @return {data-references.DataReferenceCollection[]}
     */
    listAllDataReferences() {
        return this.hasNormalization ? [this.ref, this.normalization.ref] : [this.ref];
    }
    /**
     * @return {data-references.DataRecord[]}
     */
    makeDataRecords() {
        const { variables } = this.ref;
        const references = (this.hasNormalization
            ? this.normalization.makeNormalizedDataReferences(variables)
            : variables);
        return references.map((reference, index) => {
            const override = this.overrides[index];
            return DataRecord.fromReference(reference, override);
        });
    }
    /**
     * @return {object}
     */
    toJSON() {
        return {
            ref: this.ref.toImplementationJSON(),
            overrides: this.overrides,
            selection: this.selection,
            normalization: this.normalization.isPopulated ? this.normalization : undefined,
        };
    }
    /**
     * @return {boolean}
     */
    hasCustomerData() {
        return this.ref.variables.some(dataReference => dataReference.type === DataReferenceSourceType.CUSTOMER);
    }
}
DataImplementation.Normalization = DataImplementationNormalization;
DataImplementation.Override = DataImplementationOverride;
DataImplementation.Selection = DataImplementationSelection;
export default DataImplementation;
